import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import NavBar from "@/components/NavBar";
import { ASSETS } from "@/lib/assets";
import { Crown, Swords, Map, Skull, ArrowUp, Zap, Users, Shield, Eye, EyeOff, Trophy, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LeaderboardPanel from "@/components/LeaderboardPanel";
import RankLadder from "@/components/RankLadder";
import ObjectivePanel from "@/components/ObjectivePanel";
import ReputationPanel from "@/components/ReputationPanel";
import StreakBanner from "@/components/StreakBanner";
import RivalsPanel from "@/components/RivalsPanel";
import GlobalFeedTicker from "@/components/GlobalFeedTicker";
import BloodMoonBanner from "@/components/BloodMoonBanner";
import OfficerRoster from "@/components/OfficerRoster";
import SaveSlots from "@/components/SaveSlots";
import { sfxDistantScream, isEnabled } from "@/lib/audio";

export default function Command() {
  const { state, refreshState } = useAuth();
  const navigate = useNavigate();
  const [qm, setQm] = useState(null);
  const [showSlots, setShowSlots] = useState(false);

  useEffect(() => {
    refreshState();
    api.get("/game/quartermaster").then(({ data }) => setQm(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  // Refresh after a PvP attack resolves
  useEffect(() => {
    const handler = () => refreshState();
    window.addEventListener("game-state-changed", handler);
    return () => window.removeEventListener("game-state-changed", handler);
    /* eslint-disable-next-line */
  }, []);

  // Distant scream idle ambience — fires once ~60-90s after arriving on Command
  // and then randomly every 90-180s while the player stays.
  useEffect(() => {
    let cancelled = false;
    const schedule = () => {
      if (cancelled) return;
      const delay = 60_000 + Math.random() * 90_000;
      const t = setTimeout(() => {
        if (isEnabled()) sfxDistantScream();
        schedule();
      }, delay);
      return t;
    };
    const first = schedule();
    return () => { cancelled = true; clearTimeout(first); };
  }, []);

  // Auto-redirect new players to the tutorial on first Command page load.
  useEffect(() => {
    if (state && state.tutorial_seen === false) {
      navigate("/tutorial");
    }
    /* eslint-disable-next-line */
  }, [state?.tutorial_seen]);

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center text-bone tac-label">
        SUMMONING THE DEAD...
      </div>
    );
  }

  const promote = async (accept) => {
    try {
      await api.post("/game/promotion", { accept });
      toast.success(accept ? "Promoted. The horde bows lower." : "Promotion refused. You stay in the dirt.");
      refreshState();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No promotion available.");
    }
  };

  const offered = state.offered_rank_index > state.rank_index;

  return (
    <div className="min-h-screen">
      <NavBar active="command" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10 lg:py-16">
        {/* Blood Moon banner — only renders when active */}
        <BloodMoonBanner />

        {/* Header / rank */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 fade-up">
            <p className="tac-label mb-4" data-testid="command-current-rank-label">
              YOUR RANK // {state.rank?.suggestion_weight ? `INFLUENCE ${Math.round(state.rank.suggestion_weight * 100)}%` : ""}
            </p>
            <h1 className="font-serif-h text-5xl sm:text-6xl lg:text-7xl uppercase text-bone leading-none"
                data-testid="command-rank-name">
              {state.rank?.name}
            </h1>

            {/* Daily streak banner */}
            <div className="mt-6">
              <StreakBanner />
            </div>

            {/* Next-rank requirement */}
            {state.next_rank_requirement && (
              <div className="mt-6 tactical-card" data-testid="next-rank-requirement-panel">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="tac-label">PATH TO {state.next_rank_requirement.next_rank?.toUpperCase()}</span>
                  <span className="font-mono text-[0.65rem] text-muted-h tabular-nums" data-testid="next-rank-progress-numbers">
                    {state.next_rank_requirement.kind === "horde"
                      ? `${state.next_rank_requirement.current.toLocaleString()} / ${state.next_rank_requirement.target.toLocaleString()}`
                      : `${state.next_rank_requirement.current} / ${state.next_rank_requirement.target}`}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-main)] mb-3 leading-relaxed" data-testid="next-rank-label">
                  {state.next_rank_requirement.label}.
                </p>
                <div className="bar-track">
                  <div className="bar-fill"
                       data-testid="next-rank-progress-bar"
                       style={{
                         width: `${Math.max(2, state.next_rank_requirement.progress_pct)}%`,
                         background: state.next_rank_requirement.met
                                       ? "var(--bone)"
                                       : "linear-gradient(90deg, var(--blood), #8a0a0a)",
                       }} />
                </div>
                <p className="text-[0.65rem] text-muted-h mt-2 tracking-wider" data-testid="next-rank-percent">
                  {state.next_rank_requirement.progress_pct}%
                  {state.next_rank_requirement.met && offered && " — PROMOTION OFFERED BELOW"}
                  {state.next_rank_requirement.met && !offered && " — REQUIREMENT MET"}
                </p>
                <RankLadder currentRankIdx={state.rank_index} />
              </div>
            )}

            <div className="mt-3">
              <ReputationPanel state={state} />
            </div>

            {offered && (
              <div className="mt-8 narrative-panel" data-testid="promotion-panel">
                <p className="tac-label">PROMOTION OFFERED</p>
                <p className="font-serif-h text-2xl text-bone mt-2 mb-4">
                  Become {state.offered_rank?.name}
                </p>
                <p className="text-sm text-[var(--text-main)] mb-6">
                  You may accept the higher seat — or refuse and stay where you are. The dead don't push.
                </p>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={() => promote(true)} className="btn-blood" data-testid="accept-promotion-button">
                    <ArrowUp className="w-3 h-3" /> ACCEPT
                  </button>
                  <button onClick={() => promote(false)} className="btn-bone" data-testid="decline-promotion-button">
                    REFUSE — STAY {state.rank?.name?.toUpperCase()}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-5 fade-up delay-1 grid grid-cols-2 gap-3">
            <Stat icon={Users} label="HORDE" value={state.personal_horde} testid="stat-horde" />
            <Stat icon={Zap} label="INFLUENCE" value={`${Math.round((state.rank?.suggestion_weight || 0) * 100)}%`} testid="stat-influence" />
            <Stat icon={Swords} label="WON" value={state.battles_won} testid="stat-won" />
            <Stat icon={Skull} label="LOST" value={state.battles_lost} testid="stat-lost" />
            <Stat icon={Skull} label="FALLEN" value={state.fallen_zombies} testid="stat-fallen" />
            <Stat icon={Users} label="CONVERTED" value={state.humans_converted} testid="stat-converted" />
          </div>
        </div>

        {/* Mode banner instead of morale bar */}
        <div className="mt-12 tactical-card">
          <div className="flex items-baseline justify-between mb-2">
            <span className="tac-label">BATTLE MODE</span>
            <span className="font-mono text-xs text-bone" data-testid="mode-indicator">
              {state.rank_index >= 2 ? "COMMAND // HORDE TACTICS" : "PERSONAL // BOOTS IN THE MUD"}
            </span>
          </div>
          <p className="text-xs text-muted-h mt-1 leading-relaxed">
            {state.rank_index >= 2
              ? "You command. The dead obey. Set engagement, commit forces, decide when to surround."
              : "You fight personally under a Squad Leader. Choose approach, target, and tactic for each duel."}
          </p>
        </div>

        {/* Quartermaster panel (optional) */}
        {state.quartermaster_enabled && qm && (
          <div className="mt-4 narrative-panel" data-testid="quartermaster-panel">
            <div className="flex items-center justify-between mb-3">
              <span className="tac-label">QUARTERMASTER // {qm.region_name?.toUpperCase()}</span>
              <button
                onClick={async () => {
                  await api.post("/game/quartermaster/toggle", { enabled: false });
                  refreshState();
                  toast.success("Quartermaster disabled.");
                }}
                className="btn-ghost flex items-center gap-1"
                data-testid="qm-disable-button"
              >
                <EyeOff className="w-3 h-3" /> HIDE
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {qm.upcoming.map((b, i) => (
                <div key={b.key || `${b.name}-${i}`} className={`tactical-card border-l-2 ${
                  b.is_current ? "border-blood" :
                  b.attainable ? "border-[#1B4D3E]" : "border-[var(--border-subtle)]"
                }`} data-testid={`qm-battle-${i}`}>
                  <div className="flex items-baseline justify-between">
                    <p className="font-serif-h text-lg text-bone">{b.name}</p>
                    {b.is_current && <span className="tac-label text-blood">NOW</span>}
                    {!b.is_current && !b.attainable && <span className="tac-label text-[var(--danger)]">LOCKED</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-1 mt-2 text-[0.7rem] font-mono">
                    <span className="text-muted-h">HUMANS</span><span className="text-bone text-right">{b.humans}</span>
                    <span className="text-muted-h">MIN HORDE</span>
                    <span className={`text-right ${b.attainable ? "text-bone" : "text-[var(--danger)]"}`}>{b.min_horde}</span>
                    <span className="text-muted-h">EST KILLS</span><span className="text-bone text-right">{b.expected_kills}</span>
                    <span className="text-muted-h">EST CONVERT</span><span className="text-[#7FBA9D] text-right">~{b.expected_convert}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[0.65rem] text-muted-h mt-3 tracking-wider">
              ESTIMATES ASSUME VICTORY · ACTUAL OUTCOMES VARY WITH ENGAGEMENT, COMMITMENT, MORALE & RNG
            </p>
          </div>
        )}

        {!state.quartermaster_enabled && (
          <div className="mt-4">
            <button
              onClick={async () => {
                await api.post("/game/quartermaster/toggle", { enabled: true });
                refreshState();
                const { data } = await api.get("/game/quartermaster");
                setQm(data);
                toast.success("Quartermaster active.");
              }}
              className="btn-ghost flex items-center gap-1"
              data-testid="qm-enable-button"
            >
              <Eye className="w-3 h-3" /> ENABLE QUARTERMASTER ASSIST
            </button>
          </div>
        )}

        {/* Personal Army box for Grunt → Warlord (below Crown Prince) */}
        {state.rank_index < 4 && (
          <div className="mt-4 narrative-panel" data-testid="personal-army-panel">
            <p className="tac-label mb-3">PERSONAL ARMY // YOUR HORDE vs THE {state.superior_rank_name?.toUpperCase()}'S</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-h tracking-widest">UNDER YOUR DIRECT COMMAND</p>
                <p className="font-serif-h text-4xl text-bone mt-1" data-testid="pa-personal-horde">
                  {state.personal_horde}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-h tracking-widest">
                  {state.superior_rank_name?.toUpperCase()} COMMANDS ({state.superior_mult}× YOURS)
                </p>
                <p className="font-serif-h text-4xl text-blood mt-1" data-testid="pa-superior-horde">
                  {(state.superior_horde ?? 0).toLocaleString()}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-h mt-4 leading-relaxed">
              Your superior's horde scales with yours — they always loom over you.
              Half of every personal kill you convert flows to them. Accepting promotion grants you 5% of their army as your own.
            </p>
          </div>
        )}

        {/* Enemy morale — global pulse of humanity */}
        <div className="mt-4 narrative-panel" data-testid="enemy-morale-panel">
          <div className="flex items-baseline justify-between mb-2">
            <p className="tac-label">ENEMY MORALE // THE WILL OF HUMANITY</p>
            <p className="font-mono text-sm text-bone" data-testid="enemy-morale-value">
              {Math.round(state.enemy_morale ?? 80)} / 100
              {typeof state.enemy_morale_label !== "undefined" &&
                <span className="text-blood ml-2">— {state.enemy_morale_label}</span>}
            </p>
          </div>
          <div className="bar-track">
            <div className="bar-fill"
                 style={{
                   width: `${Math.min(100, Math.max(2, state.enemy_morale ?? 80))}%`,
                   background: (state.enemy_morale ?? 80) < 30
                                ? "var(--bone)"
                                : (state.enemy_morale ?? 80) < 60
                                ? "linear-gradient(90deg, var(--blood), var(--bone))"
                                : "var(--blood)",
                 }} />
          </div>
          <p className="text-[0.7rem] text-muted-h mt-2 leading-relaxed">
            High morale → humans hold their line. As cities fall, officers drop, and your kills mount,
            the world breaks. Every region you take eats at this number.
          </p>
        </div>

        {/* Weekly horde leaderboard */}
        <div className="mt-4">
          <LeaderboardPanel readOnly={false} />
        </div>

        {/* Active objective */}
        <div className="mt-4">
          <ObjectivePanel />
        </div>

        {/* Rivals */}
        <div className="mt-4">
          <RivalsPanel />
        </div>

        {/* Officer roster */}
        <div className="mt-4">
          <OfficerRoster />
        </div>

        {/* Global feed ticker */}
        <div className="mt-4">
          <GlobalFeedTicker />
        </div>

        {/* Next battle preview */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 narrative-panel relative">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <img src={ASSETS.bone} alt="" className="w-full h-full object-cover mix-blend-overlay" />
            </div>
            <div className="relative">
              <p className="tac-label mb-3">NEXT TARGET</p>
              <h2 className="font-serif-h text-4xl text-bone uppercase mb-1" data-testid="next-battle-name">
                {state.next_battle?.battle_name}
              </h2>
              <p className="text-sm text-muted-h tracking-wider mb-6" data-testid="next-battle-region">
                {state.next_battle?.region_name} // BATTLE {(state.next_battle?.battle_index ?? 0) + 1} OF 6
              </p>
              <p className="text-base leading-relaxed text-[var(--text-main)] italic" data-testid="next-battle-intel">
                "{state.next_battle?.intel}"
              </p>

              <div className="mt-8 grid grid-cols-3 gap-2 text-xs">
                <Mini label="HUMANS" value={state.next_battle?.humans} />
                <Mini label="WEAPONS" value={`${Math.round((state.next_battle?.weapons || 0) * 100)}%`} />
                <Mini label="FORTIFIED" value={`${Math.round((state.next_battle?.fortification || 0) * 100)}%`} />
              </div>

              <button onClick={() => navigate("/battle")} className="btn-blood mt-10" data-testid="go-battle-button">
                <Swords className="w-4 h-4" /> ENGAGE
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-4">
            <ActionCard
              icon={Map}
              title="WORLD MAP"
              description={state.world_map_unlocked ? "Whisper targets to your superior. They might listen."
                                                    : "Unlocks at 50 zombies under your command."}
              onClick={() => state.world_map_unlocked && navigate("/world")}
              disabled={!state.world_map_unlocked}
              testid="card-world-map"
            />
            <ActionCard
              icon={Crown}
              title={state.king_defeated ? "THE BONE THRONE" : "REBELLION"}
              description={
                state.king_defeated ? "The throne is yours. Rule, or burn it all."
                : state.can_rebel ? "Choose: kneel, or rip the throne from the bone."
                : "Locked. Become Crown Prince first."
              }
              onClick={() => (state.can_rebel || state.king_defeated) && navigate("/rebellion")}
              disabled={!state.can_rebel && !state.king_defeated}
              testid="card-rebellion"
            />
            <ActionCard
              icon={Skull}
              title="MEMORIAL"
              description="Count the dead under your command. They will not rise again."
              onClick={() => navigate("/memorial")}
              testid="card-memorial"
            />
            <ActionCard
              icon={HelpCircle}
              title="TUTORIAL"
              description="The induction. Approach, target, tactic, ranks, the world map, and the aim of the game."
              onClick={() => navigate("/tutorial")}
              testid="card-tutorial"
            />
            <ActionCard
              icon={Trophy}
              title="HALL OF HORDES"
              description="The largest hordes ever commanded. Permanent record. Etched in stone."
              onClick={() => navigate("/hall")}
              testid="card-hall"
            />
            <ActionCard
              icon={Swords}
              title="NEW GAME"
              description="Forge a new world. Choose permadeath, map size, and stakes."
              onClick={() => navigate("/new-game")}
              testid="card-new-game"
            />
            <ActionCard
              icon={Shield}
              title="THE CRYPT — SAVE SLOTS"
              description="Entomb this horde, or raise a buried one. Run a cautious dead and a reckless dead side by side."
              onClick={() => setShowSlots(true)}
              testid="card-save-slots"
            />
          </div>
        </div>
      </div>
      <SaveSlots open={showSlots} onClose={() => setShowSlots(false)} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="tactical-card flex items-center gap-3" data-testid={testid}>
    <Icon className="w-4 h-4 text-blood" />
    <div className="flex-1 min-w-0">
      <p className="tac-label">{label}</p>
      <p className="font-mono text-base text-bone mt-0.5 truncate">{value}</p>
    </div>
  </div>
);

const Mini = ({ label, value }) => (
  <div className="tactical-card">
    <p className="tac-label">{label}</p>
    <p className="font-mono text-bone mt-0.5">{value}</p>
  </div>
);

const ActionCard = ({ icon: Icon, title, description, onClick, disabled, testid }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`tactical-card text-left flex items-start gap-4 p-5 transition-all
                ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-blood hover:bg-[#0F0707]"}`}
    data-testid={testid}
  >
    <Icon className="w-5 h-5 text-blood mt-1" />
    <div>
      <p className="tac-label">{title}</p>
      <p className="font-mono text-xs text-[var(--text-main)] mt-2 leading-relaxed">{description}</p>
    </div>
  </button>
);
